// Pestaña Amortización: el plan de pagos completo de `GET /credits/{id}/installments`. La variante
// `collections` es la de Cobranza: título plano, tabla que cabe en el panel y ayuda al pie.
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { LoanInstallmentStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanInstallmentStatusEnum';
import { cn } from '@/lib/cn';
import { formatShortDayWithYearMx } from '@/lib/dateHelpers';
import type { CreditFileBlock } from '../api/useCreditFile';
import {
    installmentCoveredCents,
    installmentDueDay,
    installmentPaidCents,
} from '@/lib/installments';
import {
    formatMoneyCents,
    installmentStatusPresentationOf,
    NO_VALUE,
    scheduleHeadlineOf,
    scheduleTallyLineOf,
    tallySchedule,
} from '../logic';
import { CreditBlock, CreditCard, CreditChip, CreditEmptyNote } from './CreditFileUi';

const CELL =
    'border-b border-border/50 px-2 py-1 text-[10.5px] align-middle tabular-nums whitespace-nowrap';
const NUM_CELL = 'text-right font-semibold';

/** El fondo tenue de la fila según el estado de la cuota (`.fk-q-*` del mock). */
const ROW_TINT: Record<LoanInstallmentStatusEnum, string> = {
    [LoanInstallmentStatusEnum.PAID]: 'bg-success-soft/50',
    [LoanInstallmentStatusEnum.PAID_IN_ADVANCE]: 'bg-success-soft/50',
    [LoanInstallmentStatusEnum.OVERDUE]: 'bg-danger-soft/50',
    [LoanInstallmentStatusEnum.RESTRUCTURED]: 'bg-info-soft/35',
    [LoanInstallmentStatusEnum.PENDING]: '',
};

const COLUMNS: readonly (readonly [label: string, numeric: boolean, width?: string])[] = [
    ['#', false, 'w-[38px]'],
    ['Vence', false, 'w-[78px]'],
    ['K', true],
    ['I', true],
    ['IVA', true],
    ['Total', true],
    ['Pagado', true],
    ['Saldo', true],
    ['Atraso', true, 'w-[50px]'],
    ['Estado', false, 'w-[78px]'],
];

/** Variante de Cobranza: fondo tenue solo en lo vencido; lo pagado va en texto apagado. */
const COLLECTIONS_ROW_TONE: Record<LoanInstallmentStatusEnum, string> = {
    [LoanInstallmentStatusEnum.PAID]: 'text-muted-foreground',
    [LoanInstallmentStatusEnum.PAID_IN_ADVANCE]: 'text-muted-foreground',
    [LoanInstallmentStatusEnum.OVERDUE]: 'bg-danger-soft/40',
    [LoanInstallmentStatusEnum.RESTRUCTURED]: 'text-muted-foreground',
    [LoanInstallmentStatusEnum.PENDING]: '',
};

/** K, I e IVA solo con el panel ancho; Atraso desde el mediano. Los montos nunca se truncan. */
const WIDE_ONLY = 'hidden @2xl:table-cell';
const MEDIUM_ONLY = 'hidden @md:table-cell';

/** Cada monto reserva lo que pide «$10,000.00»; si no cabe, la tabla se desplaza en su panel. */
const COLLECTIONS_COLUMNS: readonly (readonly [
    label: string,
    numeric: boolean,
    width?: string,
    visibility?: string,
])[] = [
    ['#', false, 'w-[28px]'],
    ['Vence', false, 'w-[60px]'],
    ['K', true, 'w-[72px]', WIDE_ONLY],
    ['I', true, 'w-[72px]', WIDE_ONLY],
    ['IVA', true, 'w-[72px]', WIDE_ONLY],
    ['Total', true, 'w-[72px]'],
    ['Pagado', true, 'w-[72px]'],
    ['Saldo', true, 'w-[72px]'],
    ['Atraso', true, 'w-[46px]', MEDIUM_ONLY],
    ['Estado', false, 'w-[60px]'],
];

export const SCHEDULE_HELP_NOTE =
    'K = capital, I = interés. Una cuota puede recibir varios pagos (parcial → completa).';

const COLLECTIONS_CELL =
    'border-b border-border/60 px-1.5 py-[3px] align-middle text-[10.5px] tabular-nums whitespace-nowrap';

/**
 * Días de atraso como los pinta el mock: solo la cuota VENCIDA los declara. Se miden sobre el día
 * de calendario del vencimiento, que convertido de zona se correría un día.
 */
function arrearsCellOf(installment: LoanInstallmentResponse, today: string): number | null {
    if (installment.status !== LoanInstallmentStatusEnum.OVERDUE || !installment.dueDate) {
        return null;
    }
    const due = installmentDueDay(installment.dueDate);
    const elapsed = Math.round(
        (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / 86_400_000,
    );
    return Math.max(1, elapsed);
}

function ScheduleRow({
    installment,
    today,
    collections = false,
    highlighted = false,
}: {
    installment: LoanInstallmentResponse;
    today: string;
    collections?: boolean;
    highlighted?: boolean;
}) {
    const presentation = installmentStatusPresentationOf(installment.status);
    const arrears = arrearsCellOf(installment, today);
    // Cobranza cuenta los parciales como Resumen; el expediente sigue derivándolo del estado.
    const paidCents = collections
        ? installmentCoveredCents(installment)
        : installmentPaidCents(installment);
    // Saldo de ESA cuota: total menos lo aplicado. Una reestructurada ya no se debe, así que es cero.
    const dueCents = paidCents === null ? 0 : installment.totalCents - paidCents;
    if (collections) {
        const muted = <span className="text-muted-foreground">{NO_VALUE}</span>;
        return (
            <tr
                aria-current={highlighted ? 'true' : undefined}
                className={cn(
                    COLLECTIONS_ROW_TONE[installment.status],
                    highlighted &&
                        'bg-info-soft font-bold text-foreground shadow-[inset_0_2px_0_hsl(var(--info)),inset_0_-2px_0_hsl(var(--info))]',
                )}
            >
                <td
                    className={cn(
                        COLLECTIONS_CELL,
                        'overflow-hidden font-mono text-muted-foreground',
                    )}
                >
                    #{installment.installmentNumber}
                </td>
                <td className={cn(COLLECTIONS_CELL, 'text-[10px]')}>
                    {installment.dueDate
                        ? formatShortDayWithYearMx(installmentDueDay(installment.dueDate))
                        : NO_VALUE}
                </td>
                <td className={cn(COLLECTIONS_CELL, WIDE_ONLY, 'text-right')}>
                    {formatMoneyCents(installment.capitalCents)}
                </td>
                <td className={cn(COLLECTIONS_CELL, WIDE_ONLY, 'text-right')}>
                    {formatMoneyCents(installment.interestCents)}
                </td>
                <td className={cn(COLLECTIONS_CELL, WIDE_ONLY, 'text-right')}>
                    {formatMoneyCents(installment.ivaCents)}
                </td>
                <td className={cn(COLLECTIONS_CELL, 'text-right font-bold')}>
                    {formatMoneyCents(installment.totalCents)}
                </td>
                <td className={cn(COLLECTIONS_CELL, 'text-right')}>
                    {paidCents === null || paidCents === 0 ? muted : formatMoneyCents(paidCents)}
                </td>
                <td className={cn(COLLECTIONS_CELL, 'text-right')}>
                    {dueCents > 0 ? formatMoneyCents(dueCents) : muted}
                </td>
                <td
                    className={cn(
                        COLLECTIONS_CELL,
                        MEDIUM_ONLY,
                        'text-right',
                        arrears !== null && arrears > 30 && 'font-bold text-danger',
                        arrears !== null && arrears <= 30 && 'font-bold text-warning',
                    )}
                >
                    {arrears === null ? NO_VALUE : `${String(arrears)}d`}
                </td>
                <td className={COLLECTIONS_CELL}>
                    <CreditChip tone={presentation.tone} className="text-[8.5px] font-medium">
                        {presentation.shortLabel}
                    </CreditChip>
                </td>
            </tr>
        );
    }
    return (
        <tr className={ROW_TINT[installment.status]}>
            <td className={cn(CELL, 'font-mono text-muted-foreground')}>
                #{installment.installmentNumber}
            </td>
            <td className={CELL}>
                {installment.dueDate
                    ? formatShortDayWithYearMx(installmentDueDay(installment.dueDate))
                    : NO_VALUE}
            </td>
            <td className={cn(CELL, NUM_CELL)}>{formatMoneyCents(installment.capitalCents)}</td>
            <td className={cn(CELL, NUM_CELL)}>{formatMoneyCents(installment.interestCents)}</td>
            <td className={cn(CELL, NUM_CELL)}>{formatMoneyCents(installment.ivaCents)}</td>
            <td className={cn(CELL, NUM_CELL, 'font-bold')}>
                {formatMoneyCents(installment.totalCents)}
            </td>
            <td className={cn(CELL, NUM_CELL)}>
                {paidCents === null || paidCents === 0 ? (
                    <span className="text-muted-foreground">{NO_VALUE}</span>
                ) : (
                    formatMoneyCents(paidCents)
                )}
            </td>
            <td className={cn(CELL, NUM_CELL)}>
                {dueCents > 0 ? (
                    formatMoneyCents(dueCents)
                ) : (
                    <span className="text-muted-foreground">{NO_VALUE}</span>
                )}
            </td>
            <td
                className={cn(
                    CELL,
                    NUM_CELL,
                    arrears !== null && arrears > 30 && 'font-bold text-danger',
                    arrears !== null && arrears <= 30 && 'font-bold text-warning',
                )}
            >
                {arrears === null ? NO_VALUE : `${String(arrears)}d`}
            </td>
            <td className={CELL}>
                <CreditChip tone={presentation.tone}>{presentation.shortLabel}</CreditChip>
            </td>
        </tr>
    );
}

export interface CreditScheduleTabProps {
    installments: CreditFileBlock<LoanInstallmentResponse[]>;
    today: string;
    /** `collections` = la de Cobranza: sin tarjeta, cabe en el panel y lleva ayuda al pie. */
    variant?: 'file' | 'collections';
    /** La cuota abierta en Cobranza, que se resalta en el plan. */
    highlightInstallmentNumber?: number;
    /** El plazo del crédito: manda sobre las cuotas que el plan ya generó al contar el total. */
    plannedTotal?: number;
}

export function CreditScheduleTab({
    installments,
    today,
    variant = 'file',
    highlightInstallmentNumber,
    plannedTotal,
}: CreditScheduleTabProps) {
    const rows = installments.data ?? [];
    const counted = tallySchedule(rows);
    const tally = plannedTotal === undefined ? counted : { ...counted, total: plannedTotal };
    const hasRows = rows.length > 0;
    if (variant === 'collections') {
        return (
            <CreditBlock
                isLoading={installments.isLoading}
                failure={installments.failure}
                subject="la amortización"
                onRetry={installments.retry}
            >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                    <h3 className="text-[11.5px] font-semibold">
                        {hasRows ? scheduleHeadlineOf(tally) : 'Desglose completo de cuotas'}
                    </h3>
                    {hasRows ? (
                        <span className="text-[10px] text-muted-foreground">
                            {scheduleTallyLineOf(tally)}
                        </span>
                    ) : null}
                </div>
                {hasRows ? (
                    <div className="@container overflow-x-auto">
                        <table className="w-full min-w-[364px] table-fixed border-collapse">
                            <thead>
                                <tr>
                                    {COLLECTIONS_COLUMNS.map(
                                        ([label, numeric, width, visibility]) => (
                                            <th
                                                key={label}
                                                scope="col"
                                                className={cn(
                                                    'overflow-hidden whitespace-nowrap border-b border-border bg-muted/40 px-1 py-[3px] text-[9px] font-semibold uppercase tracking-[0.45px] text-muted-foreground',
                                                    numeric ? 'text-right' : 'text-left',
                                                    width,
                                                    visibility,
                                                )}
                                            >
                                                {label}
                                            </th>
                                        ),
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((installment) => (
                                    <ScheduleRow
                                        key={installment.installmentNumber}
                                        installment={installment}
                                        today={today}
                                        collections
                                        highlighted={
                                            installment.installmentNumber ===
                                            highlightInstallmentNumber
                                        }
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <CreditEmptyNote>Sin cronograma generado para este crédito.</CreditEmptyNote>
                )}
                {hasRows ? (
                    <div className="@container">
                        <p className="mt-2 hidden rounded border-l-[3px] border-info bg-info-soft px-2.5 py-1.5 text-[10.5px] text-foreground @2xl:block">
                            {SCHEDULE_HELP_NOTE}
                        </p>
                    </div>
                ) : null}
            </CreditBlock>
        );
    }
    return (
        <CreditCard
            title={hasRows ? scheduleHeadlineOf(tally) : 'Desglose'}
            meta={hasRows ? scheduleTallyLineOf(tally) : undefined}
            flush
        >
            <CreditBlock
                isLoading={installments.isLoading}
                failure={installments.failure}
                subject="la amortización"
                onRetry={installments.retry}
            >
                {hasRows ? (
                    <div className="overflow-x-auto">
                        <table className="w-max min-w-[720px] border-collapse">
                            <thead>
                                <tr className="bg-muted/40">
                                    {COLUMNS.map(([label, numeric, width]) => (
                                        <th
                                            key={label}
                                            scope="col"
                                            className={cn(
                                                'whitespace-nowrap border-b border-border px-2 py-1.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground',
                                                numeric ? 'text-right' : 'text-left',
                                                width,
                                            )}
                                        >
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((installment) => (
                                    <ScheduleRow
                                        key={installment.installmentNumber}
                                        installment={installment}
                                        today={today}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <CreditEmptyNote>Sin cronograma generado para este crédito.</CreditEmptyNote>
                )}
            </CreditBlock>
        </CreditCard>
    );
}
